import {ELEMENT_TYPE_LABELS} from '../lib/constants';
import {assertNever} from '../lib/assert-never';
import type {ImageCrop, PhotoRequirement} from '../lib/types';
import {TableElement} from './TableElement';
import {
  HeaderRenderer,
  TextRenderer,
  PhotoRenderer,
  DividerRenderer,
  SignatureRenderer,
  CheckboxRenderer,
  DropdownRenderer,
  LogoRenderer,
  QrRenderer,
} from './renderers';
import {substituteVars, type InlineEditState} from './InlineLabel';
import {type FillSlotProps, type ElemProps, type SignatureValue} from './renderers/element-props';
import {buildBaseStyle} from './renderers/element-styles';
import type {PhotoPick} from '@/lib/photo-capture/image-input';

export type {InlineEditState, SignatureValue};

export function PreviewElement({
  item,
  fillMode,
  drawFilled,
  sampleData,
  inlineEdit,
  onChangeElement,
  onSelect,
  cropEditId,
  zoom,
  onCropApply,
  onCropCancel,
  fillValue,
  onFillChange,
  onFillPhoto,
  fillReadOnly,
  cellFill,
}: {
  item: PhotoRequirement;
  fillMode: boolean;
  sampleData: Record<string, string>;
  inlineEdit?: InlineEditState;
  onChangeElement?: (updated: PhotoRequirement) => void;
  onSelect?: () => void;
  cropEditId?: string | null;
  zoom?: number;
  onCropApply?: (crop: ImageCrop) => void;
  onCropCancel?: () => void;
  fillValue?: unknown;
  onFillChange?: (value: unknown) => void;
  onFillPhoto?: (pick: PhotoPick) => void;
  fillReadOnly?: boolean;
  /**
   * The unscoped slot plumbing, for container elements. Only `table` reads it:
   * its CELLS are the fill slots, so it scopes per cell itself rather than
   * taking the single pre-scoped `fillValue`/`onFillChange` every other element
   * gets. See TableElementProps.cellFill.
   */
  cellFill?: FillSlotProps;
  /**
   * Draw the element as it will be FILLED, whatever `fillMode` says about
   * authoring.
   *
   * `fillMode` used to answer two questions at once — how to draw an element,
   * and whether the canvas is an authoring surface — and the design canvas
   * needs opposite answers to them: draw the real control, keep selection and
   * drag. Conflated, an author designing a photo report saw a grey "Photo" box
   * where the crew would get an "Add photo" pill, and a grey placeholder where
   * they would get an input. The two questions are now two props, and only the
   * TABLE arm below still reads `fillMode`, because for a table the question is
   * genuinely about authoring: its structure stays editable while its cells draw
   * filled (see `EditorCellElement`).
   *
   * Defaults to `fillMode`, so a caller that has not thought about it gets what
   * it always got.
   */
  drawFilled?: boolean;
}) {
  const label = substituteVars(item.label, sampleData);
  const props: ElemProps = {
    label,
    fillMode: drawFilled ?? fillMode,
    inlineEdit,
    fillValue,
    onFillChange,
    onFillPhoto,
    fillReadOnly,
    sampleData,
    onChangeElement,
  };
  switch (item.type) {
    case 'header':
      return <HeaderRenderer {...props} item={item} />;
    case 'text':
      return <TextRenderer {...props} item={item} />;
    case 'photo':
      return <PhotoRenderer {...props} item={item} />;
    case 'divider':
      return <DividerRenderer {...props} item={item} />;
    case 'page_break':
      return null;
    // Drawn by PageNumberStamps, not here: a stamp repeats across a range of pages,
    // so only the page being rendered knows which number this instance shows.
    case 'page_number':
      return null;
    case 'signature':
      return <SignatureRenderer {...props} item={item} />;
    case 'checkbox':
      return <CheckboxRenderer {...props} item={item} />;
    case 'dropdown':
      return <DropdownRenderer {...props} item={item} />;
    case 'table':
      return (
        <TableElement
          element={item}
          onChange={onChangeElement ?? (() => {})}
          onSelect={onSelect}
          sampleData={sampleData}
          // In fill / report mode the table's STRUCTURE is fixed — no selection,
          // resize handles, expand dot, drag grips or authoring edits. Its cells
          // are still fillable: `cellFill` carries the answers in and out, and
          // each fillable cell element renders through the same renderers as an
          // element outside the table (DERP-225).
          readOnly={fillMode}
          cellFill={fillMode ? cellFill : undefined}
        />
      );
    case 'logo':
      return (
        <LogoRenderer
          {...props}
          item={item}
          cropEditId={cropEditId}
          zoom={zoom}
          onCropApply={onCropApply}
          onCropCancel={onCropCancel}
        />
      );
    case 'qr_code':
      return <QrRenderer item={item} sampleData={sampleData} />;
    case 'group':
      // Sentinel groups aren't normally rendered here (LivePreview excludes them);
      // show a labelled placeholder if one ever reaches this renderer.
      return (
        <div
          style={buildBaseStyle(item.style)}
          className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-400"
        >
          {ELEMENT_TYPE_LABELS[item.type]} — {label}
        </div>
      );
    default:
      return assertNever(item, 'element type');
  }
}
